<template>
  <ContentHeader
    title="ALM Connections"
    :learn-more-ids="['alm-getting-started']"
    :heading-level="2"
  >
    <template #actions>
      <BaseButton @click="goNext">+ Add connection</BaseButton>
    </template>
  </ContentHeader>
  <PageBody>
    <div :class="$style['alm-page']">
      <div :class="$style['toolbar']">
        <SearchInput v-model="nameFilter" placeholder="Search" />
      </div>
      <DataTableV2 :data="connections" :columns="tableColumns" :global-filter="nameFilter">
        <template #cell-name="{ row }">
          <a
            role="button"
            tabindex="0"
            :class="$style['connection-link']"
            :title="row.name"
            @click="edit(row)"
            @keydown.enter="edit(row)"
          >
            {{ row.name }}
          </a>
        </template>
        <template #cell-status="{ row }">
          <SvgIcon
            v-if="row.state === 'error'"
            name="system/alert-triangle"
            width="18"
            height="18"
            :class="$style['warning-icon']"
            tooltip="Connection to the ALM tool failed. To get more information and ability to fix it go to More actions → Events."
          />
          <SvgIcon
            v-else-if="row.state === 'okay'"
            name="system/checkmark-circled-filled"
            width="18"
            height="18"
            :class="$style['success-icon']"
            tooltip="Connection is working"
          />
        </template>
        <template #cell-action="{ row }">
          <RowActions>
            <RowActionsButton
              icon="general/edit"
              tooltip="Edit"
              aria-label="Edit"
              :data-testid="`edit-button-${row.name}`"
              @click="edit(row)"
            />
            <BaseDropdown align="right">
              <template #trigger>
                <RowActionsMenuTrigger :data-testid="`action-menu-${row.name}`" />
              </template>
              <!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
              <li tabindex="-1" role="menuitem" class="list-item" @click="edit(row)">Edit</li>
              <li tabindex="-1" role="menuitem" class="list-item" @click="removeModal(row)">
                Delete
              </li>
              <li tabindex="-1" role="menuitem" class="list-item" @click="event(row)">Events</li>
              <li
                v-for="action in extraAlmActions(row.alm_tool_type)"
                :key="action.name"
                tabindex="-1"
                role="menuitem"
                class="list-item"
                @click="action.event(row)"
              >
                {{ action.name }}
              </li>
              <!-- eslint-enable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
            </BaseDropdown>
          </RowActions>
        </template>
      </DataTableV2>

      <DeleteModal ref="delete" title="Delete ALM Connection" @delete="deleteConnection">
        {{ toDelete.alm_name }} '{{ toDelete.name }}'
      </DeleteModal>
      <ReloadWorkspaceModal
        :is-open="Boolean(reloadingWorkspace.connectionIdToSync)"
        :done="reloadingWorkspace.isDone"
        :error-message="reloadingWorkspace.errorMessage"
        @close="reloadingWorkspace.connectionIdToSync = false"
      />
    </div>
  </PageBody>
</template>

<script>
import { mapState } from "pinia";

import { almConnectionsPageAddNewButtonClicked, almConnectionsPageSeen } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import BaseButton from "@/components-ng/BaseButton.vue";
import BaseDropdown from "@/components-ng/BaseDropdown.vue";
import ContentHeader from "@/components-ng/ContentHeader.vue";
import DataTableV2 from "@/components-ng/DataTableV2.vue";
import DeleteModal from "@/components-ng/DeleteModalLegacy.vue";
import PageBody from "@/components-ng/PageBody.vue";
import RowActions from "@/components-ng/RowActions/RowActions.vue";
import RowActionsButton from "@/components-ng/RowActions/RowActionsButton.vue";
import RowActionsMenuTrigger from "@/components-ng/RowActions/RowActionsMenuTrigger.vue";
import SearchInput from "@/components-ng/SearchInput.vue";
import SvgIcon from "@/components-ng/SvgIcon.vue";
import { AlmConfigurationHandler } from "@/handlers/almConfigurationHandler";
import { SessionsHandler } from "@/handlers/sessionsHandler";
import { useAppStore } from "@/stores";
import { createHandlerContext } from "@/stores/context";

import ReloadWorkspaceModal from "./ReloadWorkspaceModal.vue";

export default {
  name: "AlmConnectionsPage",
  components: {
    ContentHeader,
    PageBody,
    ReloadWorkspaceModal,
    DeleteModal,
    BaseButton,
    BaseDropdown,
    DataTableV2,
    RowActions,
    RowActionsButton,
    RowActionsMenuTrigger,
    SearchInput,
    SvgIcon,
  },
  data() {
    return {
      nameFilter: "",
      reloadingWorkspace: {
        errorMessage: null,
        connectionIdToSync: false,
        isDone: false,
      },
      connections: [],
      toDelete: {},
      tableColumns: [
        {
          accessorKey: "name",
          header: "Name",
          enableSorting: true,
          meta: { truncate: true, style: { width: "50%", minWidth: "200px" } },
        },
        {
          accessorKey: "alm_name",
          header: "ALM Tool",
          enableSorting: true,
          enableGlobalFilter: false,
          meta: { style: { minWidth: "150px", whiteSpace: "nowrap" } },
        },
        { id: "spacer", header: "", enableSorting: false, meta: { style: { width: "100%" } } },
        {
          id: "status",
          header: "Status",
          enableSorting: false,
          meta: { style: { width: "80px" } },
        },
        { id: "action", header: "", enableSorting: false, meta: { style: { minWidth: "64px" } } },
      ],
    };
  },
  computed: {
    ...mapState(useAppStore, ["company", "session"]),
    almConfigurationHandler() {
      return new AlmConfigurationHandler(createHandlerContext());
    },
    sessionsHandler() {
      return new SessionsHandler(createHandlerContext());
    },
  },
  created() {
    this.getAlmConnections();
  },
  mounted() {
    trackEvent(almConnectionsPageSeen());
  },
  methods: {
    getAlmConnections() {
      this.almConfigurationHandler.getAlmConnections().then((cons) => {
        this.connections = cons;
      });
    },
    edit(connection) {
      this.$router.push({
        name: "editAlmConnection",
        params: { connectionId: connection.id, almType: connection.alm_type },
      });
    },
    event(connection) {
      this.$router.push({
        name: "almConnectionEvents",
        params: { connectionId: connection.id, almType: connection.alm_type },
      });
    },
    removeModal(connection) {
      this.toDelete = this.$refs.delete.show(connection);
    },
    async deleteConnection(connection) {
      const almHandler = this.$almHandler({
        almTool: connection.alm_type,
        alm_connection_id: connection.id,
      });
      const res = await almHandler.delete();
      if (res.success) {
        this.$refs.delete.close();
        this.getAlmConnections();
      } else {
        this.setSessionsNames(res.sessions_ids);
      }
    },
    setSessionsNames(ids) {
      let sessionsNames = "";
      this.sessionsHandler.getSessions().then((sessions) => {
        for (let index = 0; index < ids.length; index++) {
          sessions.forEach((singleSession) => {
            if (ids[index] === singleSession.session_id) {
              sessionsNames += singleSession.name;
              if (index !== ids.length - 1) {
                sessionsNames += ", ";
              }
            }
          });
        }
        sessionsNames += ".";
        this.$refs.delete.fail(
          `There are planning sessions using this ALM: ${sessionsNames}. Delete them first.`,
        );
      });
    },
    goNext() {
      trackEvent(almConnectionsPageAddNewButtonClicked());
      this.$router.push({ name: "AddAlmConnection" });
    },
    extraAlmActions(almToolType) {
      return [
        {
          name: "Reload Status and Workflows",
          event: async (connection) => {
            this.reloadingWorkspace.isDone = false;
            this.reloadingWorkspace.connectionIdToSync = connection.id;

            const { id: connectionId } = connection;
            try {
              await this.$almHandler({
                almTool: almToolType.toLowerCase(),
                alm_connection_id: connectionId,
              }).loadAlmItemTypes();
            } catch (err) {
              this.reloadingWorkspace.errorMessage = err.toString();
            } finally {
              this.reloadingWorkspace.isDone = true;
            }
          },
        },
      ];
    },
  },
};
</script>

<style lang="scss" module>
@use "@/colors" as c;
@use "@/styles/palette" as p;

.alm-page {
  display: flex;
  flex-direction: column;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.connection-link {
  cursor: pointer;
  color: inherit;
  text-decoration: none;
  transition: color 100ms ease;

  &:hover {
    color: c.$brand-primary;
  }
}

.warning-icon {
  color: p.$warning-50;
}

.success-icon {
  color: p.$success-50;
}
</style>
